import type { CSSProperties } from 'react';
import { memo } from 'react';
import { Skeleton } from '@mantine/core';

import classes from './TableRowSkeleton.module.css';

interface ColumnDef {
  readonly flex?: number;
  readonly width?: number;
}

interface TableRowSkeletonProps {
  readonly columns: readonly ColumnDef[];
  readonly count?: number;
  readonly showDot?: boolean;
  readonly className?: string;
}

/** Widths cycle so adjacent rows look varied, not identical. */
const SKELETON_WIDTHS = ['75%', '60%', '80%', '55%', '70%'];

/**
 * Configurable table-row skeleton matching flex-column layouts
 * used in CompactTaskRow, DelayedTaskRow, TaskListRow, NoteListRow.
 */
export const TableRowSkeleton = memo(function TableRowSkeleton({
  columns,
  count = 5,
  showDot = false,
  className,
}: TableRowSkeletonProps) {
  return (
    <div className={className} role="status" aria-busy="true">
      {Array.from({ length: count }, (_, rowIdx) => (
        <div key={rowIdx} className={classes.row}>
          {showDot && (
            <div className={classes.dotCol}>
              <Skeleton height={8} width={8} circle />
            </div>
          )}
          {columns.map((col, colIdx) => {
            const style: CSSProperties = col.flex
              ? { flex: col.flex, minWidth: 0 }
              : { width: col.width, flexShrink: 0 };

            const widthIdx = (rowIdx + colIdx) % SKELETON_WIDTHS.length;

            return (
              <div key={colIdx} style={style}>
                <Skeleton
                  height={12}
                  width={SKELETON_WIDTHS[widthIdx]}
                  radius="sm"
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
});

TableRowSkeleton.displayName = 'TableRowSkeleton';
